/**
 * Regression Plugin - Core Algorithms
 * 
 * Implements regression algorithms for scientific curve fitting.
 * Includes linear, polynomial, exponential, and non-linear models.
 * 
 * @packageDocumentation
 * @module plugins/regression
 */

import type {
  RegressionData,
  RegressionResult,
  LinearRegressionConfig,
  PolynomialRegressionConfig,
  ExponentialRegressionConfig,
  GaussianRegressionConfig
} from './types';
import { solveLinearSystem } from '../analysis/math';

// ============================================
// Utility Functions
// ============================================

function toArray(data: Float32Array | Float64Array | number[]): number[] {
  return Array.from(data);
}

function toFloat32Array(data: number[]): Float32Array {
  return new Float32Array(data);
}

function calculateMean(data: number[]): number {
  return data.reduce((sum, val) => sum + val, 0) / data.length;
}

function calculateVariance(data: number[]): number {
  const mean = calculateMean(data);
  return data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length;
}

function calculateRSquared(observed: number[], fitted: number[]): number {
  const meanObserved = calculateMean(observed);
  let ssRes = 0;
  let ssTot = 0;
  
  for (let i = 0; i < observed.length; i++) {
    ssRes += Math.pow(observed[i] - fitted[i], 2);
    ssTot += Math.pow(observed[i] - meanObserved, 2);
  }
  
  return 1 - (ssRes / ssTot);
}

function calculateAdjustedRSquared(rSquared: number, n: number, k: number): number {
  return 1 - ((1 - rSquared) * (n - 1)) / (n - k - 1);
}

function calculateRMSE(observed: number[], fitted: number[]): number {
  let sumSquaredErrors = 0;
  for (let i = 0; i < observed.length; i++) {
    sumSquaredErrors += Math.pow(observed[i] - fitted[i], 2);
  }
  return Math.sqrt(sumSquaredErrors / observed.length);
}
/*
function _calculateStatistics(data: RegressionData, predictions: Float32Array): { rSquared: number; rmse: number; mae: number; residuals: number[] } {
  const n = data.x.length;
  const yMean = Array.from(data.y).reduce((sum, y) => sum + y, 0) / n;
  
  let ssTotal = 0;
  let ssResidual = 0;
  
  for (let i = 0; i < n; i++) {
    const yActual = data.y[i];
    const yPred = predictions[i];
    
    ssTotal += Math.pow(yActual - yMean, 2);
    ssResidual += Math.pow(yActual - yPred, 2);
  }
  
  const rSquared = 1 - (ssResidual / ssTotal);
  const rmse = Math.sqrt(ssResidual / n);
  const mae = Array.from(data.y).reduce((sum, y, i) => sum + Math.abs(y - predictions[i]), 0) / n;
  
  return {
    rSquared,
    rmse,
    mae,
    residuals: Array.from(data.y).map((y, i) => y - predictions[i])
  };
}
*/
// ============================================
// Linear Regression
// ============================================

export function linearRegression(
  data: RegressionData,
  config: LinearRegressionConfig = {}
): RegressionResult {
  const startTime = performance.now();
  
  const x = toArray(data.x);
  const y = toArray(data.y);
  const weights = data.weights ? toArray(data.weights) : null;
  const { forceOrigin = false } = config;
  
  const n = x.length;
  
  if (forceOrigin) {
    // Forced through origin: y = bx
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < n; i++) {
      const weight = weights ? weights[i] : 1;
      numerator += weight * x[i] * y[i];
      denominator += weight * x[i] * x[i];
    }
    
    const slope = numerator / denominator;
    const fittedValues = x.map(xi => slope * xi);
    const residuals = y.map((yi, i) => yi - fittedValues[i]);
    
    const rSquared = calculateRSquared(y, fittedValues);
    const adjustedRSquared = calculateAdjustedRSquared(rSquared, n, 1);
    const rmse = calculateRMSE(y, fittedValues);
    
    return {
      method: 'linear',
      parameters: {
        parameters: [slope],
        uncertainties: [Math.sqrt(denominator / (n * numerator))], // Simplified
        correlationMatrix: [[1]]
      },
      statistics: {
        rSquared,
        adjustedRSquared,
        rmse,
        rss: residuals.reduce((sum, r) => sum + r * r, 0),
        tss: y.reduce((sum, yi) => sum + Math.pow(yi - calculateMean(y), 2), 0),
        n,
        k: 1
      },
      fittedValues: toFloat32Array(fittedValues),
      residuals: toFloat32Array(residuals),
      goodnessOfFit: rSquared > 0.9 ? 'excellent' : rSquared > 0.7 ? 'good' : rSquared > 0.5 ? 'fair' : 'poor',
      converged: true,
      iterations: 1,
      processingTime: performance.now() - startTime
    };
  } else {
    // Standard linear regression: y = ax + b
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    let sumWeights = 0;
    
    for (let i = 0; i < n; i++) {
      const weight = weights ? weights[i] : 1;
      sumX += weight * x[i];
      sumY += weight * y[i];
      sumXY += weight * x[i] * y[i];
      sumX2 += weight * x[i] * x[i];
      sumWeights += weight;
    }
    
    const meanX = sumX / sumWeights;
    const meanY = sumY / sumWeights;
    
    const slope = (sumXY - sumWeights * meanX * meanY) / (sumX2 - sumWeights * meanX * meanX);
    const intercept = meanY - slope * meanX;
    
    const fittedValues = x.map(xi => slope * xi + intercept);
    const residuals = y.map((yi, i) => yi - fittedValues[i]);
    
    const rSquared = calculateRSquared(y, fittedValues);
    const adjustedRSquared = calculateAdjustedRSquared(rSquared, n, 2);
    const rmse = calculateRMSE(y, fittedValues);
    
    return {
      method: 'linear',
      parameters: {
        parameters: [slope, intercept],
        uncertainties: [0.1, 0.1], // Simplified
        correlationMatrix: [[1, -0.5], [-0.5, 1]] // Simplified
      },
      statistics: {
        rSquared,
        adjustedRSquared,
        rmse,
        rss: residuals.reduce((sum, r) => sum + r * r, 0),
        tss: y.reduce((sum, yi) => sum + Math.pow(yi - meanY, 2), 0),
        n,
        k: 2
      },
      fittedValues: toFloat32Array(fittedValues),
      residuals: toFloat32Array(residuals),
      goodnessOfFit: rSquared > 0.9 ? 'excellent' : rSquared > 0.7 ? 'good' : rSquared > 0.5 ? 'fair' : 'poor',
      converged: true,
      iterations: 1,
      processingTime: performance.now() - startTime
    };
  }
}

// ============================================
// Polynomial Regression
// ============================================

export function polynomialRegression(
  data: RegressionData,
  config: PolynomialRegressionConfig
): RegressionResult {
  const startTime = performance.now();
  
  const x = toArray(data.x);
  const y = toArray(data.y);
  const { degree } = config;
  
  const n = x.length;
  const m = degree + 1; // Number of parameters
  
  // Build Vandermonde matrix
  const X: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    for (let j = 0; j <= degree; j++) {
      row.push(Math.pow(x[i], j));
    }
    X.push(row);
  }
  
  // Solve using least squares (simplified implementation)
  // In practice, would use QR decomposition or SVD
  const parameters = solveLeastSquares(X, y, 0);
  
  const fittedValues = x.map(xi => {
    let value = 0;
    for (let j = 0; j <= degree; j++) {
      value += parameters[j] * Math.pow(xi, j);
    }
    return value;
  });
  
  const residuals = y.map((yi, i) => yi - fittedValues[i]);
  
  const rSquared = calculateRSquared(y, fittedValues);
  const adjustedRSquared = calculateAdjustedRSquared(rSquared, n, m);
  const rmse = calculateRMSE(y, fittedValues);
  
  return {
    method: 'polynomial',
    parameters: {
      parameters,
      uncertainties: new Array(m).fill(0.1), // Simplified
      correlationMatrix: Array(m).fill(0).map(() => Array(m).fill(0)) // Simplified
    },
    statistics: {
      rSquared,
      adjustedRSquared,
      rmse,
      rss: residuals.reduce((sum, r) => sum + r * r, 0),
      tss: y.reduce((sum, yi) => sum + Math.pow(yi - calculateMean(y), 2), 0),
      n,
      k: m
    },
    fittedValues: toFloat32Array(fittedValues),
    residuals: toFloat32Array(residuals),
    goodnessOfFit: rSquared > 0.9 ? 'excellent' : rSquared > 0.7 ? 'good' : rSquared > 0.5 ? 'fair' : 'poor',
    converged: true,
    iterations: 10, // Simplified
    processingTime: performance.now() - startTime
  };
}

// ============================================
// Exponential Regression
// ============================================

export function exponentialRegression(
  data: RegressionData,
  _config: ExponentialRegressionConfig = {}
): RegressionResult {
  const startTime = performance.now();
  
  const x = toArray(data.x);
  const y = toArray(data.y);
  
  // Transform to linear: ln(y - offset) = ln(amplitude) + rate * x
  // Need to estimate offset first (simplified)
  const offset = Math.min(...y) - 1;
  
  const transformedY = y.map(yi => {
    const adjusted = yi - offset;
    return adjusted > 0 ? Math.log(adjusted) : 0;
  });
  
  // Linear regression on transformed data
  const linearResult = linearRegression(
    { x, y: transformedY },
    { forceOrigin: false }
  );
  
  const [rate, logAmplitude] = linearResult.parameters.parameters;
  const amplitude = Math.exp(logAmplitude);
  
  const fittedValues = x.map(xi => amplitude * Math.exp(rate * xi) + offset);
  const residuals = y.map((yi, i) => yi - fittedValues[i]);
  
  const rSquared = calculateRSquared(y, fittedValues);
  const adjustedRSquared = calculateAdjustedRSquared(rSquared, x.length, 3);
  const rmse = calculateRMSE(y, fittedValues);
  
  return {
    method: 'exponential',
    parameters: {
      parameters: [amplitude, rate, offset],
      uncertainties: [0.1, 0.01, 0.1], // Simplified
      correlationMatrix: [[1, 0.5, 0.2], [0.5, 1, 0.3], [0.2, 0.3, 1]] // Simplified
    },
    statistics: {
      rSquared,
      adjustedRSquared,
      rmse,
      rss: residuals.reduce((sum, r) => sum + r * r, 0),
      tss: y.reduce((sum, yi) => sum + Math.pow(yi - calculateMean(y), 2), 0),
      n: x.length,
      k: 3
    },
    fittedValues: toFloat32Array(fittedValues),
    residuals: toFloat32Array(residuals),
    goodnessOfFit: rSquared > 0.9 ? 'excellent' : rSquared > 0.7 ? 'good' : rSquared > 0.5 ? 'fair' : 'poor',
    converged: true,
    iterations: 5, // Simplified
    processingTime: performance.now() - startTime
  };
}

// ============================================
// Gaussian Regression
// ============================================

export function gaussianRegression(
  data: RegressionData,
  _config: GaussianRegressionConfig = {}
): RegressionResult {
  const startTime = performance.now();
  
  const x = toArray(data.x);
  const y = toArray(data.y);
  
  // Non-linear fitting using simplified gradient descent
  let amplitude = Math.max(...y);
  let mean = x.reduce((sum, xi) => sum + xi, 0) / x.length;
  let std = Math.sqrt(calculateVariance(x));
  let offset = Math.min(...y);
  
  const learningRate = 0.001;
  const maxIterations = 100;
  const tolerance = 1e-6;
  
  for (let iter = 0; iter < maxIterations; iter++) {
    let gradAmplitude = 0, gradMean = 0, gradStd = 0, gradOffset = 0;
    let totalError = 0;
    
    for (let i = 0; i < x.length; i++) {
      const xi = x[i];
      const yi = y[i];
      
      // Gaussian function: A * exp(-((x - μ)² / (2σ²))) + offset
      const exponent = -Math.pow(xi - mean, 2) / (2 * std * std);
      const predicted = amplitude * Math.exp(exponent) + offset;
      const error = yi - predicted;
      totalError += error * error;
      
      // Gradients
      const expTerm = Math.exp(exponent);
      gradAmplitude += error * expTerm;
      gradMean += error * amplitude * expTerm * (xi - mean) / (std * std);
      gradStd += error * amplitude * expTerm * Math.pow(xi - mean, 2) / (Math.pow(std, 3));
      gradOffset += error;
    }
    
    // Update parameters
    amplitude += learningRate * gradAmplitude;
    mean += learningRate * gradMean;
    std += learningRate * gradStd;
    offset += learningRate * gradOffset;
    
    // Ensure std is positive
    std = Math.max(std, 0.01);
    
    if (totalError < tolerance) break;
  }
  
  const fittedValues = x.map(xi => {
    const exponent = -Math.pow(xi - mean, 2) / (2 * std * std);
    return amplitude * Math.exp(exponent) + offset;
  });
  
  const residuals = y.map((yi, i) => yi - fittedValues[i]);
  
  const rSquared = calculateRSquared(y, fittedValues);
  const adjustedRSquared = calculateAdjustedRSquared(rSquared, x.length, 4);
  const rmse = calculateRMSE(y, fittedValues);
  
  return {
    method: 'gaussian',
    parameters: {
      parameters: [amplitude, mean, std, offset],
      uncertainties: [0.1, 0.1, 0.01, 0.1], // Simplified
      correlationMatrix: Array(4).fill(0).map(() => Array(4).fill(0)) // Simplified
    },
    statistics: {
      rSquared,
      adjustedRSquared,
      rmse,
      rss: residuals.reduce((sum, r) => sum + r * r, 0),
      tss: y.reduce((sum, yi) => sum + Math.pow(yi - calculateMean(y), 2), 0),
      n: x.length,
      k: 4
    },
    fittedValues: toFloat32Array(fittedValues),
    residuals: toFloat32Array(residuals),
    goodnessOfFit: rSquared > 0.9 ? 'excellent' : rSquared > 0.7 ? 'good' : rSquared > 0.5 ? 'fair' : 'poor',
    converged: true,
    iterations: maxIterations,
    processingTime: performance.now() - startTime
  };
}

// ============================================
// Helper Functions
// ============================================

function solveLeastSquares(X: number[][], y: number[], regularization: number = 0): number[] {
  const n = X.length;
  const m = X[0].length;
  
  // Compute X^T * X
  const XtX: number[][] = Array(m).fill(0).map(() => Array(m).fill(0));
  const Xty: number[] = Array(m).fill(0);
  
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < m; j++) {
      for (let k = 0; k < m; k++) {
        XtX[j][k] += X[i][j] * X[i][k];
      }
      Xty[j] += X[i][j] * y[i];
    }
  }
  
  // Add L2 regularization if needed
  if (regularization > 0) {
    for (let i = 0; i < m; i++) {
      XtX[i][i] += regularization;
    }
  }
  
  // Solve using Gaussian elimination
  return solveLinearSystem(XtX, Xty);
}